import dayjs from "dayjs";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import Datatable from "./Datatable";
import DatepickerInput from "./DatepickerInput";
import { useDispatch } from "../../store/DataProvider";
import { handleApiData, saveStaffMonth } from "../../store/actions";
import { businessDate, asDayString } from "../../services/utility";

import "react-datepicker/dist/react-datepicker.css";

/* Four, and the order is the order they happen in: the two that settle
   something already earned, then the two that are earned as they are paid. */
const PAYMENT_KINDS = [
    { value: "commission", label: "Commission" },
    { value: "salary", label: "Salary" },
    { value: "extra", label: "Extra" },
    { value: "bonus", label: "Bonus" },
];

const Commissions = ({
    commissionTotals,
    selectedStaffId,
    selectedStaffName,
    selectedMonth,
    payments,
    payable,
    days,
}) => {
    const customStyles = {
        headRow: {
            style: {
                fontWeight: "bold",
            },
        },
        rows: {
            style: {
                fontSize: "14px",
            },
        },
    };
    const paymentsColumns = [
        {
            name: "Paid Date",
            column: "day",
            sortable: "true",
            width: "36%",
            compact: "true",
            center: "true",
        },
        /* what it was for, so an Extra is distinguishable from a salary
           instalment in the list rather than only in the total above it.
           Blank on the payments that predate types, which is honest. */
        {
            name: "For",
            column: "kind",
            sortable: "true",
            width: "32%",
            compact: "true",
            center: "true",
        },
        {
            name: "Amount Paid",
            column: "paid",
            sortable: "true",
            width: "32%",
            compact: "true",
            center: "true",
        },
    ];
    const paymentsDt = {
        columns: paymentsColumns,
        data: payments,
        customStyles,
    };

    const dispatch = useDispatch();
    /* the working day, not the calendar one -- see MonthlyExpenses */
    const today = businessDate().toISOString();

    /* A date inside the month being looked at. The picker is already bound to
       that month by minDate/maxDate, but the field defaulted to today
       regardless -- so viewing July in September showed a date the picker
       itself would have refused, and paying filed the money into September
       while the screen still said July. The month is taken off the day, so
       the payment lands wherever the date says.
       The current month keeps today; a month that is over takes its last
       day, which is when a month's pay is settled. */
    const dateWithinMonth = () => {
        const month = selectedMonth?.value;
        if (!month || dayjs(today).format("YYYY-MM") === month) {
            return today;
        }
        return dayjs(month).endOf("month").toISOString();
    };

    const [commissionDate, setCommissionDate] = useState(dateWithinMonth);
    const [commissionPayment, setCommissionPayment] = useState("");
    const [buttonMessage, setButtonMessage] = useState();
    const [buttonDisabled, setButtonDisabled] = useState(true);
    /* Which payment the form is correcting, if any. The same form does both
       jobs, as on the expenses screen -- a second one would be the same two
       fields over again. */
    const [editing, setEditing] = useState(null);
    /* held as typed, so the box can be emptied on the way to another number
       without the count snapping back to the derived one mid-keystroke */
    const [daysInput, setDaysInput] = useState("");
    /* What the money is for. Commission and Salary settle something already
       earned and change no figure; Extra and Bonus are earned in the moment
       of being paid, so they count as earnings too. Not siblings, and Tom
       decided that saying so on screen would confuse more than it clarified
       -- so they read as four plain choices and the difference lives in what
       the figures do. Blank is the fifth state, and what every payment
       before today is. */
    const [paymentKind, setPaymentKind] = useState("");

    const getButtonMessage = () => {
        const missing = [];
        if (!commissionPayment) {
            missing.push("payment amount");
        }
        if (!commissionDate) {
            missing.push("date");
        }
        /* Required, and blank to start with rather than guessed at from how
           the person is paid. A pre-picked answer would be accepted without
           being read, and the whole reason this field exists is that a ฿350
           special commission went in as an ordinary payment with nothing to
           prompt a second thought. Choosing is the point. */
        if (!paymentKind) {
            missing.push("what it is for");
        }
        if (missing.length) {
            /* commas between all but the last, as on the expense button */
            const last = missing.pop();
            const list = missing.length
                ? `${missing.join(", ")} and ${last}`
                : last;
            return { disable: true, message: `Enter ${list}` };
        } else if (editing) {
            /* the date says which payment is being changed -- it is what the
               row shows, so it still matches the highlighted line when the
               amount is the thing being corrected */
            return {
                disable: false,
                message: `Save payment of ${asDayString(commissionDate)}`,
            };
        } else {
            return {
                disable: false,
                message: `Pay ${selectedStaffName} THB ${commissionPayment}`,
            };
        }
    };

    useEffect(() => {
        const { disable, message } = getButtonMessage();
        setButtonMessage(message);
        setButtonDisabled(disable);
    }, [commissionPayment, commissionDate, editing, paymentKind]);

    /* the date follows the month on screen, so a payment cannot be filed
       into a month you are not looking at */
    useEffect(() => {
        setCommissionDate(dateWithinMonth());
    }, [selectedMonth?.value]);

    /* follows the month and the staff member, so switching either shows that
       month's count rather than the last one looked at */
    useEffect(() => {
        setDaysInput(
            typeof days?.daysWorked === "number" ? String(days.daysWorked) : "",
        );
    }, [days?.daysWorked, selectedStaffId, selectedMonth?.value]);

    const onChangeDays = value => {
        if (value && !/^\d{0,2}$/.test(value)) {
            return;
        }
        setDaysInput(value);
    };

    const onSaveDays = () => {
        saveStaffMonth(
            {
                staffId: selectedStaffId,
                month: selectedMonth.value,
                daysWorked: daysInput,
            },
            dispatch,
        );
    };

    const onChangePayment = e => {
        const value = e.target.value;
        if (value && !Number(value)) {
            return;
        }
        setCommissionPayment(value);
    };

    const dtpMinMax = [
        dayjs(selectedMonth?.value).startOf("month").valueOf(),
        dayjs(selectedMonth?.value).endOf("month").valueOf(),
    ];

    const handleSubmitPayment = e => {
        e.preventDefault();
        const payment = {
            staffId: selectedStaffId,
            commissionDate,
            /* the day as it stands in the picker, so the server files it under
               that rather than re-reading the instant in its own timezone */
            day: asDayString(commissionDate),
            commissionPayment,
            /* absent when entering, which is what makes one handler do both */
            commissionId: editing?._id,
            type: paymentKind,
        };
        setButtonDisabled(true);
        setCommissionDate("");
        setCommissionPayment("");
        setEditing(null);
        setPaymentKind("");
        handleApiData(payment, dispatch, "submitCommission");
        setCommissionDate(dateWithinMonth());
    };

    /* Clicking a payment loads it into the form above rather than deleting
       it. Deleting on a single click was the last place in the app that still
       did that, and it sat on a list of money handed to people. */
    const onClickPayment = row => {
        setEditing(row);
        setCommissionDate(dayjs(row.day).toISOString());
        setCommissionPayment(String(row.amount ?? ""));
        setPaymentKind(row.type || "");
    };

    const stopEditing = () => {
        setEditing(null);
        setCommissionPayment("");
        setPaymentKind("");
        setCommissionDate(dateWithinMonth());
    };

    const handleDeletePayment = () => {
        const { _id, paid, day } = editing;
        if (
            !window.confirm(
                `Delete the ${paid} paid to ${selectedStaffName} on ${day}?\n\nThis cannot be undone.`,
            )
        ) {
            return;
        }
        stopEditing();
        handleApiData(
            { _id, month: selectedMonth.value },
            dispatch,
            "deleteCommission",
        );
    };

    /* the payment being edited, so the form and the list agree */
    const paymentRowStyles = [
        {
            when: row => Boolean(editing) && row._id === editing._id,
            style: {
                backgroundColor: "var(--row-selected)",
                fontWeight: "bold",
            },
        },
    ];

    return (
        <>
            <div className="staff-pay-table">
                <Datatable settings={commissionTotals} />
            </div>
            {/* Salary is days times a rate, and both are shown: a wage
                figure should never be a number with no workings behind it.
                The count comes from the days that carry a sale, which has
                matched reality every time it has been checked -- but it is
                editable, because a day worked and not sold on would
                otherwise quietly cost someone a day's pay. */}
            {days?.salaried ? (
                <div className="days-worked-row">
                    <span className="commission-text">Days worked:</span>
                    <input
                        className="days-worked-input"
                        type="text"
                        value={daysInput}
                        placeholder={String(days.derivedDays)}
                        onChange={e => onChangeDays(e.target.value)}
                    />
                    <span className="days-worked-note">
                        {days.overridden
                            ? `corrected — the sales show ${days.derivedDays}`
                            : `from the days with a sale`}
                    </span>
                    {daysInput !== String(days.daysWorked) ? (
                        <button
                            type="button"
                            className="days-worked-save"
                            onClick={onSaveDays}
                        >
                            Save
                        </button>
                    ) : null}
                </div>
            ) : null}

            {payable ? (
                <div
                    className="commission-submit-row"
                    style={{
                        borderBottom: payments.length
                            ? "thin solid rgba(0,0,0,.12)"
                            : "none",
                    }}
                >
                    <div className="spacer-div">&nbsp;</div>
                    {/* each label is wrapped with its own field, so that when
                        the two cannot share a line they stack as whole pairs
                        rather than leaving a label stranded above its input */}
                    <div className="commission-submit-container">
                        <div className="commission-field">
                            <span className="commission-text">Date:</span>
                            {/* A button, not a text field -- the same input
                                the expense row, the sale editor and the till
                                already use, and now the last of the four to
                                get it. As a text field, tapping it on an iPad
                                raised the keyboard, and one stray character
                                blanked the date and left the button saying
                                "Enter date" with nothing to say why. There is
                                nothing to type into now; the calendar is the
                                only way in, and it offers this month alone. */}
                            <DatePicker
                                selected={commissionDate}
                                onChange={date => setCommissionDate(date)}
                                dateFormat="YYYY-MM-dd"
                                placeholderText="Select date"
                                minDate={dtpMinMax[0]}
                                maxDate={dtpMinMax[1]}
                                customInput={
                                    <DatepickerInput className="commission-date-button" />
                                }
                            />
                        </div>
                        <div className="commission-field">
                            <span className="commission-text">Amount:</span>
                            <input
                                className="commission-input"
                                type="text"
                                placeholder="Payment"
                                value={commissionPayment}
                                onChange={onChangePayment}
                            />
                        </div>
                    </div>

                    {/* What the money is for. Four plain choices: the first
                        two record what a payment settled and change no
                        figure, the last two are earned in the moment of
                        being paid and so count as earnings as well. Leaving
                        it unset is allowed and is what every payment before
                        today is. */}
                    <div className="payment-kind-group">
                        {PAYMENT_KINDS.map(({ value, label }) => (
                            <div key={value}>
                                <input
                                    type="radio"
                                    className="payment-kind-input"
                                    name="payment-kind"
                                    id={`payment-kind-${value}`}
                                    checked={paymentKind === value}
                                    onChange={() => setPaymentKind(value)}
                                />
                                <label
                                    className="payment-kind-label"
                                    htmlFor={`payment-kind-${value}`}
                                >
                                    {label}
                                </label>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: "5px" }}>
                        <button
                            type="button"
                            className="submit-commission"
                            disabled={buttonDisabled}
                            onClick={handleSubmitPayment}
                            style={{
                                opacity: buttonDisabled ? 0.4 : 1,
                                cursor: buttonDisabled ? "default" : "cursor",
                            }}
                        >
                            {buttonMessage}
                        </button>
                    </div>

                    {/* only while correcting one, so recording a payment
                        stays the one-button job it was */}
                    {editing ? (
                        <div className="expenses-edit-actions">
                            <button
                                type="button"
                                className="expenses-cancel-button"
                                onClick={stopEditing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="admin-orphan-button"
                                onClick={handleDeletePayment}
                            >
                                Delete
                            </button>
                        </div>
                    ) : null}

                    <div
                        className="spacer-div"
                        style={{
                            borderTop: "thin solid rgba(0,0,0,.12)",
                            marginTop: "10px",
                        }}
                    >
                        &nbsp;
                    </div>
                    {payments ? (
                        <Datatable
                            settings={paymentsDt}
                            clickFunc={onClickPayment}
                            conditionalRowStyles={paymentRowStyles}
                        />
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

export default Commissions;
